import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getOrderUnpaidBalance, logger, orderHasInProgressAction, registerOrderAction } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { useContext, useEffect, useReducer, useRef } from 'react';
import { Button } from '../../../subcomponents/button/button';
import { ButtonColor } from '../../../subcomponents/button/types/color';
import { ButtonSize } from '../../../subcomponents/button/types/size';
import { LoadingIndicator } from '../../../subcomponents/loading-indicator/loading-indicator';
import { LoadingIndicatorType } from '../../../subcomponents/loading-indicator/types/type';
import { OrderPaymentFormContextInstance } from '../types/context';
import { createPaymentClient } from '../utils/create-payment-client';
import { OrderPaymentFormEmbeddedPaymentConnectorActionType as ActionType } from '../utils/embedded-payment-connector-action-type';
import { OrderPaymentFormEmbeddedPaymentConnectorActionReducer } from '../utils/embedded-payment-connector-reducer';
export var OrderPaymentFormEmbeddedPaymentConnectorLoadingState;
(function (OrderPaymentFormEmbeddedPaymentConnectorLoadingState) {
    OrderPaymentFormEmbeddedPaymentConnectorLoadingState["LoadingConnector"] = "LoadingConnector";
    OrderPaymentFormEmbeddedPaymentConnectorLoadingState["ProcessingPayment"] = "ProcessingPayment";
    OrderPaymentFormEmbeddedPaymentConnectorLoadingState["Success"] = "Success";
})(OrderPaymentFormEmbeddedPaymentConnectorLoadingState || (OrderPaymentFormEmbeddedPaymentConnectorLoadingState = {}));
export const defaultOrderPaymentFormEmbeddedPaymentConnectorState = {
    paymentClient: undefined,
    paymentButtonState: { isDisabled: true },
    loadingState: OrderPaymentFormEmbeddedPaymentConnectorLoadingState.LoadingConnector
};
const b = bemClassBuilder('v-order-payment-form-connector');
export const OrderPaymentFormEmbeddedPaymentConnector = observer(props => {
    const [state, dispatch] = useReducer(OrderPaymentFormEmbeddedPaymentConnectorActionReducer, defaultOrderPaymentFormEmbeddedPaymentConnectorState);
    const { paymentMethodId, onInitialisationFailed, onPaymentSuccessful, onPaymentFailed } = props;
    const { paymentClient, loadingState, paymentButtonState } = state;
    const { displayText, assets, config, events } = useContext(OrderPaymentFormContextInstance);
    const containerRef = useRef(null);
    // The connector will be re-loaded whenever the remainingAmount changes (e.g. when a gift card is added/removed)
    const remainingAmount = getOrderUnpaidBalance();
    const isDisabled = orderHasInProgressAction();
    useEffect(() => {
        (async () => {
            try {
                dispatch({ type: ActionType.LoadingPaymentClient });
                const client = createPaymentClient({
                    paymentMethodId,
                    containerRef,
                    childContainerClassName: b.element('child-container'),
                    redirectReturnUrl: config.redirectReturnUrl,
                    onPaymentSuccessful,
                    onPaymentFailed,
                    dispatch
                });
                const deregisterAction = registerOrderAction('initialise-payment-connector');
                await client.initialiseConnector();
                deregisterAction();
                dispatch({ type: ActionType.LoadedPaymentClient, paymentClient: client });
            }
            catch (error) {
                logger.error(`Failed to initialise payment connector with ID '${paymentMethodId}'. ${error}`);
                onInitialisationFailed();
            }
        })();
    }, [
        paymentMethodId,
        config.redirectReturnUrl,
        remainingAmount,
        onPaymentSuccessful,
        onPaymentFailed,
        onInitialisationFailed
    ]);
    const handlePaymentButtonClicked = () => {
        var _a;
        if (!paymentClient)
            throw new Error('Payment client has not been initialised.');
        if (isDisabled)
            return;
        (_a = events.onPaymentStarted) === null || _a === void 0 ? void 0 : _a.call(events);
        paymentClient.startPayment();
    };
    const showPaymentButton = paymentClient && !paymentClient.isPaymentButtonIncluded;
    const isLoading = loadingState !== OrderPaymentFormEmbeddedPaymentConnectorLoadingState.Success;
    return (_jsxs("div", Object.assign({ className: b.block() }, { children: [loadingState === OrderPaymentFormEmbeddedPaymentConnectorLoadingState.LoadingConnector && (_jsx(LoadingIndicator, { iconName: assets.connectorLoadingIconName, type: LoadingIndicatorType.Spinner })), loadingState === OrderPaymentFormEmbeddedPaymentConnectorLoadingState.ProcessingPayment && (_jsx(LoadingIndicator, { iconName: assets.processingPaymentIconName, message: displayText.processingPaymentMessage, type: LoadingIndicatorType.Spinner })), _jsxs("div", Object.assign({ className: b.element('wrapper', { loading: isLoading, disabled: isDisabled }) }, { children: [_jsx("div", { className: b.element('container'), ref: containerRef }), showPaymentButton && (_jsx(Button, { className: b.element('payment-button'), disabled: paymentButtonState.isDisabled || isDisabled, size: ButtonSize.Large, color: ButtonColor.Primary, label: displayText.paymentButtonLabel, onClick: handlePaymentButtonClicked, loading: loadingState === OrderPaymentFormEmbeddedPaymentConnectorLoadingState.ProcessingPayment }))] }))] })));
});
OrderPaymentFormEmbeddedPaymentConnector.displayName = 'OrderPaymentFormEmbeddedPaymentConnector';
